import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getCurrentUser } from '../services/api';
import {
  LayoutDashboard,
  MessageSquare,
  Building2,
  Wrench,
  FolderOpen,
  Star,
  MapPin,
  LogOut,
  Menu,
  X,
  Users,
  BookOpen
} from 'lucide-react';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('cms_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    getCurrentUser()
      .then(res => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('cms_token');
        navigate('/admin/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('cms_token');
    navigate('/admin/login');
  };

  // Base nav items for all users
  const baseNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/quotes', icon: MessageSquare, label: 'Quote Requests' },
    { path: '/admin/company', icon: Building2, label: 'Company Info' },
    { path: '/admin/services', icon: Wrench, label: 'Services' },
    { path: '/admin/projects', icon: FolderOpen, label: 'Projects' },
    { path: '/admin/testimonials', icon: Star, label: 'Testimonials' },
    { path: '/admin/articles', icon: BookOpen, label: 'Blog Articles' },
    { path: '/admin/service-areas', icon: MapPin, label: 'Service Areas' }
  ];

  // Add Users page for admin role only
  const navItems = user?.role === 'admin' 
    ? [...baseNavItems, { path: '/admin/users', icon: Users, label: 'User Management' }]
    : baseNavItems;

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
            <span className="text-slate-900 font-bold">R</span>
          </div>
          <span className="font-bold">CMS</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-slate-900 text-white
          transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:min-h-screen
        `}>
          {/* Logo */}
          <div className="p-6 border-b border-slate-800 hidden lg:block">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <span className="text-slate-900 font-black text-lg">R</span>
              </div>
              <div>
                <div className="font-bold">ROOFTOPPER</div>
                <div className="text-xs text-slate-400">Content Management</div>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive(item)
                    ? 'bg-amber-500 text-slate-900'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-lg font-bold">{user.username?.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <div className="font-medium">{user.username}</div>
                <div className="text-xs text-slate-400 capitalize">{user.role}</div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
